import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const CqcCentreOverview = () => (
  <Page
    title="CQC Centre Overview"
    intro="The CQC Centre continuously scores your inspection readiness against a CQC-aligned framework, using evidence AmbuNet already holds - training, DBS, appraisals, policies, governance activity, the risk register, incidents, feedback, fleet, and medicines."
  >
    <Section title="What it is">
      <p>
        Open <DocLink url="/cqc-centre" text="CQC Centre" /> from the sidebar
        under Governance. The dashboard shows an overall readiness score out
        of 100 (banded Green from 85, Amber from 65, Red below), a score for
        each CQC key question - Safe, Effective, Caring, Responsive, and
        Well-led - and counts of open Critical, Important, and Advisory gaps.
        Your top gaps and most recent evidence are listed underneath.
      </p>
      <Warning title="A self-assessment, not a CQC rating">
        The score reflects how complete and current your evidence is against a
        practical CQC-aligned framework. It is not a CQC rating and does not
        predict or guarantee any CQC judgement.
      </Warning>
      <Info title="Who can do this?">
        Every CQC Centre page requires the Governance team (site
        administrators always have access), and the CQC Centre is a tier 2
        feature.
      </Info>
    </Section>

    <Section title="How the score is kept current">
      <Bullets>
        <Bullet>
          Evidence and gaps are recomputed automatically when the dashboard is
          opened and the last calculation is more than an hour old.
        </Bullet>
        <Bullet>
          Use <strong>Recalculate</strong> to force a fresh calculation - for
          example straight after fixing a gap.
        </Bullet>
        <Bullet>
          Scores are built from CQC's full set of 34 quality statements
          grouped under the five key questions. Click a key question card to
          see its gaps.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Compliance Questions">
      <p>
        <DocLink url="/cqc-centre/compliance" text="Compliance Questions" />{' '}
        is a regulation-by-regulation self-assessment covering the 2014
        Fundamental Standards (Regulations 9-20A), the 2009 Registration
        Regulations (Statement of Purpose, Registered Manager, statutory
        notifications), and CQC's independent-ambulance inspection focus
        areas. Questions are tailored to your regulated activities - Treatment
        of disease, disorder or injury and/or Transport services (PTS) - based
        on what you record in the Registration Centre.
      </p>
      <Bullets>
        <Bullet>
          Most questions are answered with free text, file attachments, and
          links to the policies, SOPs, and Crew Vault cards that evidence
          your position. Some are answered directly by the real thing: the
          Statement of Purpose question embeds the builder itself, the
          registration question embeds the regulated activities tracker, and
          the Registered Manager, Nominated Individual, and fit-and-proper
          persons questions are answered by selecting the actual staff
          members.
        </Bullet>
        <Bullet>
          Answers must be reviewed at least annually. Overdue reviews - and
          unanswered questions - appear as gaps and required actions until
          refreshed, and every answer, edit, and review is kept in an audit
          history.
        </Bullet>
        <Bullet>
          Answered questions count as evidence toward the relevant quality
          statement, and the full self-assessment is included in the
          Inspection Pack.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Settings">
      <p>
        From <DocLink url="/cqc-centre/settings" text="CQC Centre Settings" />{' '}
        set your registration status - CQC Registered, Applying for CQC
        Registration, or using the CQC Centre voluntarily - and manage which
        quality statements count towards your score.
      </p>
      <Warning title="Deactivating a quality statement needs a reason">
        Deactivating a quality statement removes it from the readiness score
        entirely, so a written reason is mandatory. The reason stays visible
        in settings and can appear in the exported Inspection Pack - it
        cannot be used to silently inflate the score.
      </Warning>
    </Section>

    <Section title="The Inspection Pack">
      <p>
        <DocLink url="/cqc-centre/pack" text="Inspection Pack" /> (also
        reachable via <strong>Inspection Pack</strong> on the dashboard)
        builds a shareable PDF containing what an inspector needs to see:
        readiness scores, your registration and regulated activities, the full
        compliance self-assessment with answers and review dates, workforce
        assurance (DBS, training matrix, appraisals), the fleet register with
        MOT/servicing/insurance currency, policy and SOP registers, the risk
        register, incident and complaint summaries, and the complete evidence
        index.
      </p>
    </Section>

    <Section title="Related pages">
      <Bullets>
        <Bullet>
          Day-to-day work happens in{' '}
          <DocLink
            docsPage
            url="/clinical-governance/cqc-evidence-gaps-actions"
            text="Evidence, Gaps, and Actions"
          />
          .
        </Bullet>
        <Bullet>
          Whether you need to be registered at all - and the 6 December 2027
          events deadline - is handled in the{' '}
          <DocLink
            docsPage
            url="/clinical-governance/cqc-registration-centre"
            text="Registration Centre"
          />
          .
        </Bullet>
      </Bullets>
    </Section>
  </Page>
)

export default CqcCentreOverview
